import { type BlockConfig, type BlockField, linkField } from './_types';

/**
 * Payload `Block` config sidecar for the `finalCta` block.
 *
 * Decomposed from three call sites: `home/FinalCTA.tsx` (primary variant),
 * `howitworks/FinalCTA.tsx` (gradient variant with subheadline + footnote),
 * `b2b/B2BCTA.tsx` (dark variant with two-button CTA).
 *
 * Slug + interfaceName naming reconciliation:
 *   File name:               `final-cta.tsx` / `final-cta.payload.ts`
 *   React component export:  `FinalCta`
 *   Payload `slug`:          `finalCta`
 *   Payload `interfaceName`: `FinalCtaBlock`
 */

// Button text + link are intentionally NOT marked required so a CMS
// author can leave a CTA group off without a per-row boolean toggle.
// The React block guards via isCtaPresent() (text + link both non-empty).
//
// Factory returns a FRESH array on every call so primaryCta + secondaryCta
// groups don't share an array reference. At Payload cutover the runtime
// may mutate field configs to add generated ids / resolve admin config;
// a shared array would cause duplicate ids or cross-group corruption.
const buttonGroupFields = (): BlockField[] => [
  { name: 'text', type: 'text', label: 'Button text' },
  linkField({ name: 'link', label: 'Button link' }),
  {
    name: 'variant',
    type: 'select',
    label: 'Variant',
    options: [
      { label: 'Default (dark)', value: 'default' },
      { label: 'Primary (yellow)', value: 'primary' },
      { label: 'Outline', value: 'outline' },
      { label: 'Ghost', value: 'ghost' },
      { label: 'Inverted (for primary-bg surface)', value: 'inverted' },
    ],
  },
  {
    name: 'external',
    type: 'checkbox',
    defaultValue: false,
    label: 'External link?',
  },
];

export const FinalCtaBlock: BlockConfig = {
  slug: 'finalCta',
  interfaceName: 'FinalCtaBlock',
  labels: {
    singular: 'Final CTA',
    plural: 'Final CTA Blocks',
  },
  fields: [
    {
      name: 'headline',
      type: 'textarea',
      required: true,
      label: 'Headline',
      admin: { rows: 2 },
    },
    {
      name: 'subheadline',
      type: 'textarea',
      label: 'Subheadline',
      admin: { rows: 3 },
    },
    {
      name: 'variant',
      type: 'select',
      defaultValue: 'gradient',
      label: 'Variant',
      options: [
        { label: 'Primary (yellow)', value: 'primary' },
        { label: 'Dark', value: 'dark' },
        { label: 'Gradient', value: 'gradient' },
      ],
    },
    {
      name: 'roundedCard',
      type: 'checkbox',
      defaultValue: false,
      label: 'Rounded card framing',
      admin: {
        description:
          'When enabled, wraps the section in a `rounded-3xl mx-4 lg:mx-8 my-8` card -- matches the home variant.',
      },
    },
    {
      name: 'primaryCta',
      type: 'group',
      label: 'Primary CTA',
      admin: {
        description:
          'Optional. Leave both Button text + Button link blank to render the CTA section without a primary button. The block treats a CTA as "present" only when both fields are non-empty.',
      },
      fields: buttonGroupFields(),
    },
    {
      name: 'secondaryCta',
      type: 'group',
      label: 'Secondary CTA',
      admin: {
        description:
          'Optional. Leave both Button text + Button link blank to render the CTA section without a secondary button.',
      },
      fields: buttonGroupFields(),
    },
    {
      name: 'footnote',
      type: 'text',
      label: 'Footnote',
      admin: {
        description:
          'Optional small print rendered below the CTAs (e.g. "No credit card required" or "No commitment required").',
      },
    },
  ],
};

export default FinalCtaBlock;
